import * as d3 from 'd3';
import { RegulationActionColor } from '../styles/color';
import squarePath, { biggestPart } from './pathToSquare';
import _ from 'lodash';
import { isTablet } from '../styles/size';

const FILL_MAP_COLOR = 'crimson';
const BASE_MAP_COLOR = 'gainsboro';
const INTERVAL = 250;

const CONTINENT_MAP = {
  NA: 'อเมริกาเหนือ',
  AS: 'เอเชีย',
  AF: 'แอฟริกา',
  EU: 'ยุโรป',
  OC: 'โอเชียเนีย',
  SA: 'อเมริกาใต้',
};

function PlasticMap(geojson, ref, setYear, setTooltipContent) {
  // state
  let path, projection, zoomCountries, year, step;

  const $container = d3.select(ref.current),
    $gMap = $container.select('g.map'),
    $gBar = $container.select('g.bar-axis'),
    $gBarX = $gBar.select('g.x'),
    $gBarY = $gBar.select('g.y');

  let w, h;
  _updateDimenstion();

  _updateProjection(w, h);
  _updateShape('geo');
  let $path = $gMap
    .selectAll('path')
    .data(geojson.features)
    .join('path');

  function _renderMap(t) {
    $path
      .attr('fill', BASE_MAP_COLOR)
      .on('mouseenter', (feature) => {
        setTooltipContent(feature);
      })
      .transition(t)
      .attr('stroke', 'white')
      .call(_drawPath);
    return t;
  }

  function _updateProjection(w, h) {
    const long = Math.min(w, 2 * h);
    const SCALE = 0.15 * long;
    projection = d3
      .geoNaturalEarth1()
      .scale([SCALE])
      .translate([w / 2, h / 2]);

    if (path) path.projection(projection);
  }

  function _updateShape(shape) {
    path = shape === 'geo' ? d3.geoPath(projection) : squarePath(projection);
  }

  function _updateDimenstion() {
    const parent = ref.current.parentElement.parentElement;

    w = parent.offsetWidth;
    h = parent.offsetHeight - (isTablet(w) ? 50 : 0);
  }

  function _drawPath($sel) {
    $sel.attr('stroke-width', isTablet(w) ? 0.1 : 1).attr('d', path);
    // .attr("vector-effect", isTablet(w) ? "non-scaling-stroke" : "")
    // .attr("fill", ({ properties: { regulation } }) => {
    //   return RegulationActionColor(regulation?.action)
    // })
  }

  function _removePath($) {
    $.attr('d', '');
  }

  function _zoomTo($container, ZOOMSCALE = 0.85) {
    if (!zoomCountries) return $container.attr('transform', '');
    const selection = {
      type: 'FeatureCollection',
      features: geojson.features.filter(({ id }) => zoomCountries.includes(id)),
    };

    const b = path.bounds(selection);

    const zoomScale =
      ZOOMSCALE / Math.max((b[1][0] - b[0][0]) / w, (b[1][1] - b[0][1]) / h);

    const lonCenter = (b[0][0] + b[1][0]) / 2;
    const latCenter = (b[0][1] + b[1][1]) / 2;
    const center = [lonCenter, latCenter];

    const translate = [
      zoomScale * -center[0] + w / 2,
      zoomScale * -center[1] + h / 2,
    ];

    let transform = `translate(${translate[0]}, ${translate[1]}) scale(${zoomScale})`;
    $container.attr('transform', transform);
  }

  function fillFactory({ year, action, color }) {
    function fill(feature) {
      const {
        properties: { regulation },
      } = feature;

      const latestRegulation = regulation
        .filter((r) => r.year <= year)
        .sort((a, b) => b.year - a.year)[0];

      return color(latestRegulation);
    }
    return fill;
  }

  function fillByRegulation(action) {
    return function(reg) {
      return reg?.action === action
        ? RegulationActionColor(reg?.action)
        : reg
        ? 'rgba(220, 20, 60, .15)'
        : BASE_MAP_COLOR;
    };
  }

  function _turnAxisOff() {
    $gBarY.selectAll('*').remove();
    $gBarX.selectAll('*').remove();
  }
  const STEP = {};
  STEP.plasticwaste3 = function(direction) {
    $path.call(_removePath);
  };

  STEP.regulation1 = function(direction) {
    const color = (reg) => (reg ? FILL_MAP_COLOR : BASE_MAP_COLOR);
    const t = d3
      .transition()
      .delay(500)
      .duration(500);
    const t2 = _renderMap(t)
      .transition()
      .duration(500);

    _updateShape('geo');

    // change zoom state to sweden
    zoomCountries = ['SWE'];
    if (!isTablet(w)) {
      const parent = ref.current.parentElement.parentElement;
      w = parent.offsetWidth / 2;
    }

    year = 1970;
    setYear(year);

    // transition using zoomTo
    // on transition end fill countries by `year` with regulation color
    $container
      .transition(t2)
      .call(_zoomTo)
      .on(
        'end',
        _.once(() =>
          $path.attr(
            'fill',
            fillFactory({
              year,
              color,
            })
          )
        )
      );
  };

  STEP.regulation2 = function(direction) {
    const color = (reg) => (reg ? FILL_MAP_COLOR : BASE_MAP_COLOR);
    zoomCountries = ['DNK'];
    if (!isTablet(w)) {
      const parent = ref.current.parentElement.parentElement;
      w = parent.offsetWidth / 2;
    }

    year = 1994;
    setYear(year);

    $path
      .filter((feature) => zoomCountries.includes(feature.id))
      .attr('stroke', 'transparent');
    // transition using zoomTo
    // on transition end fill countries by `year` with regulation color
    $container
      .transition()
      .call(_zoomTo)
      .on(
        'end',
        _.once(() =>
          $path.attr(
            'fill',
            fillFactory({
              year,
              color,
            })
          )
        )
      );
  };

  STEP.regulation3 = function(direction, leave = false) {
    const color = (reg) => (reg ? FILL_MAP_COLOR : BASE_MAP_COLOR);
    zoomCountries = null;
    $container
      .transition()
      .duration(1000)
      .call(_zoomTo);

    const interval = d3.interval(() => {
      if (year >= 2009) interval.stop();
      else {
        year += 1;
        setYear(year);
        $path.attr(
          'fill',
          fillFactory({
            year,
            color,
          })
        );
      }
    }, INTERVAL);

    if (leave) {
      interval.stop();
      year = 2009;
      setYear(2009);
      $path.attr(
        'fill',
        fillFactory({
          year,
          color,
        })
      );
    }
  };

  STEP.regulationTHA1 = function(direction) {
    const color = (reg) => (reg ? FILL_MAP_COLOR : BASE_MAP_COLOR);
    if (direction === 'down') STEP.regulation3(undefined, true);
    zoomCountries = ['THA'];
    if (!isTablet(w)) {
      const parent = ref.current.parentElement.parentElement;
      w = parent.offsetWidth / 2;
    }
    year = 2009;
    setYear(year);

    $container
      .transition()
      .call(_zoomTo)
      .on(
        'end',
        _.once(() =>
          $path.attr(
            'fill',
            fillFactory({
              year,
              color,
            })
          )
        )
      );
  };

  STEP.regulation3cont = function(direction, leave = false) {
    const color = (reg) => (reg ? FILL_MAP_COLOR : BASE_MAP_COLOR);
    zoomCountries = null;
    $container
      .transition()
      .duration(1000)
      .call(_zoomTo);

    const interval = d3.interval(() => {
      if (year >= 2019) interval.stop();
      else {
        year += 1;
        setYear(year);
        $path.attr(
          'fill',
          fillFactory({
            year,
            color,
          })
        );
      }
    }, INTERVAL);

    if (leave) {
      interval.stop();
      year = 2019;
      setYear(2019);
      $path.attr(
        'fill',
        fillFactory({
          year,
          color,
        })
      );
    }
  };

  STEP.regulation4 = function(direction) {
    // if (direction === "down") STEP.regulation3(undefined, true)
    const color = (reg) => (reg ? FILL_MAP_COLOR : BASE_MAP_COLOR);

    year = 2019;
    setYear(year);
    $path.attr(
      'fill',
      fillFactory({
        year,
        color,
      })
    );
  };

  STEP.regulation5 = function(direction) {
    const color = fillByRegulation(
      'การห้ามผลิต แจก ใช้ และ/หรือ ซื้อขายอย่างเด็ดขาดหรือบางส่วน'
    );
    $path.transition().attr(
      'fill',
      fillFactory({
        year,
        color,
      })
    );
  };

  STEP.regulation6 = function(direction) {
    const color = fillByRegulation('เครื่องมือทางเศรษฐศาสตร์');
    $path.transition().attr(
      'fill',
      fillFactory({
        year,
        color,
      })
    );
  };

  STEP.regulation7 = function(direction) {
    const color = fillByRegulation(
      'การผสมกันระหว่างเครื่องมือทางเศรษฐศาสตร์และการห้าม'
    );
    $path.transition().attr(
      'fill',
      fillFactory({
        year,
        color,
      })
    );
  };

  STEP.regulation8 = function(direction) {
    _updateDimenstion();
    const color = fillByRegulation('ข้อตกลงระหว่างภาครัฐและภาคเอกชน');
    $path
      .transition()
      .call(_drawPath)
      .attr(
        'fill',
        fillFactory({
          year,
          color,
        })
      );
  };

  STEP.regulation9 = function(direction) {
    const DURATION = 500;

    _turnAxisOff();

    _updateShape('geo');
    $path
      .transition()
      .duration(DURATION)
      .call(_drawPath)
      .attr('transform', 'translate(0, 0)');

    setTimeout(() => {
      $path = $gMap
        .selectAll('path')
        .data(geojson.features)
        .join('path');
      $path.attr('stroke', 'white').on('mouseenter', (feature) => {
        setTooltipContent(feature);
      });
      $path
        .attr('opacity', 1)
        .attr('transform', 'translate(0, 0)')
        .attr(
          'fill',
          fillFactory({
            year,
            color: (reg) =>
              reg ? RegulationActionColor(reg?.action) : BASE_MAP_COLOR,
          })
        )
        .call(_drawPath);
    }, DURATION + 50);
  };

  STEP.regulationBar = function(direction, leave) {
    if (direction === 'up') {
      $container
        .attr('opacity', 0)
        .transition()
        .attr('opacity', 1);
    }
    // barchart
    const records = geojson.features
      .map((f) => {
        const { regulation } = f.properties;
        const latestRegulation = regulation
          .filter((r) => r.year <= year)
          .sort((a, b) => b.year - a.year)[0];
        return latestRegulation;
      })
      .filter((d) => !!d);
    const bardata = d3
      .nest()
      .key((d) => d.continent)
      .entries(records);

    // sort each continent by action
    const actionOrder = {
      'การห้ามผลิต แจก ใช้ และ/หรือ ซื้อขายอย่างเด็ดขาดหรือบางส่วน': 1,
      เครื่องมือทางเศรษฐศาสตร์: 2,
      การผสมกันระหว่างเครื่องมือทางเศรษฐศาสตร์และการห้าม: 3,
      ข้อตกลงระหว่างภาครัฐและภาคเอกชน: 4,
    };

    bardata.forEach((con) =>
      con.values.sort((a, b) => (a?.type === 'ระดับประเทศ' ? 1 : -1))
    );
    bardata.forEach((con) =>
      con.values.sort((a, b) => actionOrder[a.action] - actionOrder[b.action])
    );

    _updateDimenstion();
    const barWidth = (31 * projection.scale()) / 10;
    const marginH = (w - barWidth) / 2;

    const barHeight = (3 * 6 * projection.scale()) / 10;
    const marginV = (h - barHeight) / 2;
    const s = barHeight / ((6 * projection.scale()) / 10);

    const Y = d3
      .scaleBand()
      .domain(bardata.map((d) => d.key))
      .range([barHeight + marginV, marginV])
      .paddingInner(1 - 1 / s)
      .paddingOuter(0.1);

    const X = d3
      .scalePoint()
      .domain(d3.range(31))
      .range([marginH, marginH + (31 * projection.scale()) / 10]);

    $gBarY.attr('transform', `translate(${marginH}, 0)`);
    $gBarX.attr('transform', `translate(0, ${h - marginV})`);

    function _drawBarAxis(t) {
      const $X = t ? $gBarX.transition(t) : $gBarX;
      const $Y = t ? $gBarY.transition(t) : $gBarY;

      $Y.call(d3.axisLeft(Y).tickFormat((con) => CONTINENT_MAP[con]));
      $X.call(d3.axisBottom(X).tickValues([5, 10, 15, 20, 25, 30]));
    }

    let withReg = _.cloneDeep(geojson.features);
    withReg = withReg.filter((feature) => {
      if (feature.properties.regulation.length === 0) return false;
      else if (feature.properties.regulation[0].year > 2019) return false;
      return true;
    });
    withReg.forEach((feature) => {
      feature.geometry = {
        type: 'Polygon',
        coordinates: [biggestPart(feature.geometry.coordinates)],
      };
    });

    $path = $gMap.selectAll('path').data(withReg);
    $path.call(_drawPath);
    _updateShape('square');
    $path.exit().remove();
    $path
      .attr(
        'fill',
        fillFactory({
          year,
          color: (reg) =>
            reg ? RegulationActionColor(reg?.action) : BASE_MAP_COLOR,
        })
      )
      .call(($) => {
        $.attr('opacity', (feature) => {
          const latestRegulation = feature.properties.regulation
            .filter((r) => r.year <= year)
            .sort((a, b) => b.year - a.year)[0];

          return latestRegulation?.type === 'ระดับประเทศ' ? 1 : 0.5;
        });

        if (direction === 'down') {
          $ = $.sort((a, b) => {
            const aidx = Object.keys(CONTINENT_MAP).indexOf(
              a.properties.regulation[0]?.continent
            );
            const bidx = Object.keys(CONTINENT_MAP).indexOf(
              b.properties.regulation[0]?.continent
            );
            return aidx - bidx;
          })
            .transition()
            .duration(1500)
            .delay((d, i) => i * 10);
        }
        $.call(_drawPath).attr('transform', (feature) => {
          // this `path` is expected to be path from pathToSquare.js
          // note: this center is for normalize all country to the same 0,0
          const [cx, cy] = path.center(feature);

          // bar chart stuff
          let barposition = {};
          bardata.forEach((con, conidx) => {
            const countryidx = con.values.findIndex(
              (e) => e.iso3 === feature.id
            );
            if (countryidx !== -1) {
              barposition.continentidx = con.key;
              barposition.countryidx = countryidx;
            }
          });

          const by = Y(barposition.continentidx);
          const bx = X(barposition.countryidx);
          // const bx = (barposition.countryidx * projection.scale()) / 10
          return `translate(${bx - cx}, ${by - cy})`;
        });

        if (direction === 'down') {
          $.on(
            'end',
            _.once(() => {
              const t = d3.transition();
              _drawBarAxis(t);
            })
          );
        } else {
          _drawBarAxis();
        }
        if (leave) $.interrupt();
      });
  };

  STEP.regulationSeperator = function(direction) {
    if (direction === 'down') STEP.regulationBar('up', true);
    $container
      .attr('opacity', 1)
      .transition()
      .attr('opacity', 0);
  };

  STEP.regulationKenya = function(direction) {
    $container
      .attr('opacity', 0)
      .transition()
      .attr('opacity', 1);
    _turnAxisOff();

    zoomCountries = ['KEN'];
    // make zoomed countries stick to the left
    if (!isTablet(w)) {
      const parent = ref.current.parentElement.parentElement;
      w = parent.offsetWidth / 2;
    }

    _updateShape('geo');
    $path = $gMap
      .selectAll('path')
      .data(geojson.features)
      .join('path');
    $path.attr('stroke', 'white').on('mouseenter', (feature) => {
      setTooltipContent(feature);
    });
    $path
      .attr('opacity', 1)
      // .transition()
      .attr('transform', 'translate(0, 0)')
      .attr(
        'fill',
        fillFactory({
          year,
          color: (reg) => {
            return zoomCountries.includes(reg?.iso3)
              ? RegulationActionColor(reg?.action)
              : BASE_MAP_COLOR;
          },
        })
      )
      .call(_drawPath)
      .call(_zoomTo, 0.2);
  };

  STEP.regulationHKG = function(direction) {
    zoomCountries = ['HKG'];
    // make zoomed countries stick to the left
    if (!isTablet(w)) {
      const parent = ref.current.parentElement.parentElement;
      w = parent.offsetWidth / 2;
    }

    _updateShape('geo');
    $path = $gMap
      .selectAll('path')
      .data(geojson.features)
      .join('path');
    $path.attr('stroke', 'white').on('mouseenter', (feature) => {
      setTooltipContent(feature);
    });

    $path
      .attr('opacity', 1)
      .transition()
      .duration(1000)
      .attr('transform', 'translate(0, 0)')
      .attr(
        'fill',
        fillFactory({
          year,
          color: (reg) => {
            return zoomCountries.includes(reg?.iso3)
              ? RegulationActionColor(reg?.action)
              : BASE_MAP_COLOR;
          },
        })
      )
      .call(_drawPath)
      .call(_zoomTo, 0.4);

    $path
      .filter((feature) => zoomCountries.includes(feature.id))
      .attr('stroke', 'transparent');
  };

  STEP.regulationCHN = function(direction) {
    zoomCountries = ['CHN'];
    // make zoomed countries stick to the left
    if (!isTablet(w)) {
      const parent = ref.current.parentElement.parentElement;
      w = parent.offsetWidth / 2;
    }

    _updateShape('geo');
    $path = $gMap
      .selectAll('path')
      .data(geojson.features)
      .join('path');
    $path.attr('stroke', 'white').on('mouseenter', (feature) => {
      setTooltipContent(feature);
    });

    $path
      .filter((feature) => zoomCountries.includes(feature.id))
      .attr('stroke', 'transparent');

    $path
      .attr('opacity', 1)
      .transition()
      .duration(1000)
      .attr('transform', 'translate(0, 0)')
      .attr(
        'fill',
        fillFactory({
          year,
          color: (reg) => {
            return zoomCountries.includes(reg?.iso3)
              ? RegulationActionColor(
                  'การผสมกันระหว่างเครื่องมือทางเศรษฐศาสตร์และการห้าม'
                )
              : BASE_MAP_COLOR;
          },
        })
      )
      .call(_drawPath)
      .call(_zoomTo, 0.85);
  };

  STEP.regulationAUT = function(direction) {
    zoomCountries = ['AUT'];
    // make zoomed countries stick to the left
    if (!isTablet(w)) {
      const parent = ref.current.parentElement.parentElement;
      w = parent.offsetWidth / 2;
    }

    _updateShape('geo');
    $path = $gMap
      .selectAll('path')
      .data(geojson.features)
      .join('path');
    $path.attr('stroke', 'white').on('mouseenter', (feature) => {
      setTooltipContent(feature);
    });

    $path
      .filter((feature) => zoomCountries.includes(feature.id))
      .attr('stroke', 'transparent');

    $path
      .attr('opacity', 1)
      .transition()
      .duration(1000)
      .attr('transform', 'translate(0, 0)')
      .attr(
        'fill',
        fillFactory({
          year,
          color: (reg) => {
            return zoomCountries.includes(reg?.iso3)
              ? RegulationActionColor(reg?.action)
              : BASE_MAP_COLOR;
          },
        })
      )
      .call(_drawPath)
      .call(_zoomTo, 0.2);
  };

  STEP.regulationTHA = function() {
    zoomCountries = ['THA'];
    // make zoomed countries stick to the left
    if (!isTablet(w)) {
      const parent = ref.current.parentElement.parentElement;
      w = parent.offsetWidth / 2;
    }

    _updateShape('geo');
    $path = $gMap
      .selectAll('path')
      .data(geojson.features)
      .join('path');
    $path.attr('stroke', 'white').on('mouseenter', (feature) => {
      setTooltipContent(feature);
    });

    $path.filter((feature) => zoomCountries.includes(feature.id));

    $path
      .attr('opacity', 1)
      .transition()
      .duration(1000)
      .attr('transform', 'translate(0, 0)')
      .attr(
        'fill',
        fillFactory({
          year,
          color: (reg) => {
            return zoomCountries.includes(reg?.iso3)
              ? RegulationActionColor(reg?.action)
              : BASE_MAP_COLOR;
          },
        })
      )
      .call(_drawPath)
      .call(_zoomTo, 0.4);
  };

  const map = {};
  map.handleStepChange = function(scrollEvent) {
    const { element, index, direction } = scrollEvent;
    step = d3.select(element).attr('data-step');
    if (typeof STEP[step] === 'function') STEP[step](direction);
  };

  map.resize = function() {
    _updateDimenstion();
    _updateProjection(w, h);
    if (typeof STEP[step] === 'function') STEP[step]('up');
  };

  return map;
}

export default PlasticMap;
